import { useState, useMemo, useEffect } from "react";
import { venues } from "../data";
import {
  DAYS, MONTHS, API,
  formatTime, toLocalDateStr, isEventPast, isWorkshopPast,
  getDaysInMonth, getFirstDay, isSameDay, getWeekDates, workshopDateRange, workshopsForDate,
} from "./calendarUtils";
import { PasswordModal, AdminEditPanel, AdminEventModal } from "./AdminComponents";
import { EventModal, WorkshopBookingModal } from "./BookingComponents";
import "./YogaCalendar.css";


// ─── Event Pill ───────────────────────────────────────────
function EventPill({ event, onClick, compact, adminMode, cancelled, dateStr }) {
  const isOnline = event.venue === "venue_online";
  const isHybrid = event.isHybrid === true;
  const past = !adminMode && dateStr ? isEventPast(dateStr, event.time, event.duration) : false;
  const clickable = adminMode || (!cancelled && !past);
  return (
    <button
      onClick={() => { if (clickable) onClick(event); }}
      className={`event-pill ${isHybrid?"hybrid":isOnline?"online":"inperson"} ${compact?"compact":""} ${cancelled?"cancelled-pill":""} ${adminMode?"admin-pill":""} ${past && !adminMode?"past-pill":""}`}
      style={!clickable ? {cursor:"default", pointerEvents:"none"} : {}}
    >
      <span className="event-time">{formatTime(event.time)}</span>
      {!compact && <span className="event-title">{event.title}</span>}
      <span className={`event-tag ${isOnline?"tag-online":"tag-studio"}`}>
        {cancelled?"Cancelled":isOnline?"Online":"Studio"}
      </span>
    </button>
  );
}

function WorkshopBanner({ workshop, dateStr, onClick, adminMode }) {
  const allDates = workshopDateRange(workshop);
  const isFirst  = allDates[0] === dateStr;
  const isLast   = allDates[allDates.length - 1] === dateStr;
  const dayData  = workshop.days.find(d => d.date === dateStr);
  const sessCount = dayData?.sessions?.length ?? 0;
  const past = !adminMode && isWorkshopPast(workshop);
  return (
    <button
      onClick={() => { if (past && !adminMode) return; onClick(workshop); }}
      className={`workshop-banner ${isFirst?"ws-first":""} ${isLast?"ws-last":""} ${past && !adminMode?"past-pill":""}`}
      style={past && !adminMode ? {cursor:"default", pointerEvents:"none"} : {}}
      title={workshop.title}
    >
      {isFirst
        ? <span className="ws-banner-title">{workshop.title}</span>
        : sessCount > 0 && <span className="ws-banner-sessions">{sessCount} session{sessCount!==1?"s":""}</span>
      }
    </button>
  );
}
// ─── Main Calendar ────────────────────────────────────────
export default function YogaCalendar({ initialAdminMode = false, hideAdmin = false }) {
  const today = new Date();

  const [viewMode, setViewMode]           = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 480 ? "week" : "month"
  );
  const [currentDate, setCurrentDate]     = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [focusDay, setFocusDay]           = useState(today);
  const [classes, setClasses] = useState([]);
  const [cancelledDates, setCancelledDates] = useState(new Set());
  const [dateOverrides, setDateOverrides] = useState(new Map());
  const [workshops, setWorkshops] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [adminMode] = useState(initialAdminMode);
  const [showPwModal, setShowPwModal]     = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingClassFromModal, setEditingClassFromModal] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate]   = useState(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDay(year, month);

  // ── Mobile detection (≤480px → 2-day week view)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 480
  );

  useEffect(() => {
  async function fetchSchedule() {
    try {
      const [classRes, wsRes, cancelRes, overrideRes] = await Promise.all([
        fetch(`${API}/schedule/classes`),
        fetch(`${API}/schedule/workshops`),
        fetch(`${API}/schedule/cancellations`),
        fetch(`${API}/schedule/overrides`),
      ]);
      const classData    = await classRes.json();
      const wsData       = await wsRes.json();
      const cancelData   = await cancelRes.json();
      const overrideData = await overrideRes.json();

      setClasses(classData.classes ?? []);
      setWorkshops(wsData.workshops ?? []);
      setCancelledDates(new Set(
        (cancelData.cancellations ?? []).map(c => `${c.classId}__${c.date}`)
      ));
      setDateOverrides(new Map(
        (overrideData.overrides ?? []).map(o => [`${o.classId}__${o.date}`, { venue: o.venue }])
      ));
    } catch (e) {
      console.error("Failed to load schedule:", e);
    } finally {
      setScheduleLoading(false);
    }
  }
  fetchSchedule();
}, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    const handler = e => {
      setIsMobile(e.matches);
      // Auto-switch view when crossing the breakpoint
      setViewMode(v => {
        if (e.matches && v === "month") return "week";
        if (!e.matches && v === "week") return "month";
        return v;
      });
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const prevMonth = () => setCurrentDate(new Date(year,month-1,1));
  const nextMonth = () => setCurrentDate(new Date(year,month+1,1));

  // weekDates: 7 days on desktop, 2 days on mobile (focusDay + next)
  const weekDates = useMemo(() => {
    if (isMobile) {
      return [0, 1].map(offset => {
        const d = new Date(focusDay);
        d.setDate(focusDay.getDate() + offset);
        return d;
      });
    }
    return getWeekDates(focusDay);
  }, [focusDay, isMobile]);

  const weekStep = isMobile ? 2 : 7;
  const prevWeek = ()=>{ const d=new Date(focusDay); d.setDate(d.getDate()-weekStep); setFocusDay(d); };
  const nextWeek = ()=>{ const d=new Date(focusDay); d.setDate(d.getDate()+weekStep); setFocusDay(d); };
  const prevDay  = ()=>{ const d=new Date(focusDay); d.setDate(d.getDate()-1); setFocusDay(d); };
  const nextDay  = ()=>{ const d=new Date(focusDay); d.setDate(d.getDate()+1); setFocusDay(d); };

  const calendarCells = useMemo(()=>{
    const cells=[];
    for(let i=0;i<firstDay;i++) cells.push(null);
    for(let d=1;d<=daysInMonth;d++) cells.push(new Date(year,month,d));
    while(cells.length%7!==0) cells.push(null);
    return cells;
  },[year,month,daysInMonth,firstDay]);

const getVisibleClasses = (date) => {
  const dow = date.getDay(), dateStr = toLocalDateStr(date);
  return classes
    .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate))
    .map(c => {
      const key = `${c.id}__${dateStr}`;
      const override = dateOverrides.get(key);
      return { ...c, ...(override || {}), _cancelled: cancelledDates.has(key), _overridden: !!override };
    });
};

const getPublicClasses = (date) => {
  const dow = date.getDay(), dateStr = toLocalDateStr(date);
  return classes
    .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate))
    .map(c => {
      const key = `${c.id}__${dateStr}`;
      const override = dateOverrides.get(key);
      return { ...c, ...(override || {}), _cancelled: cancelledDates.has(key) };
    });
};

  const closeModal = () => { setSelectedEvent(null); setSelectedDate(null); setSelectedWorkshop(null); };

const handleSaveClass = async (form) => {
  const isNew = !classes.find(c => c.id === form.id);
  if (isNew) {
    const newClass = { ...form, id: `class_${Date.now()}`, startDate: toLocalDateStr(new Date()) };
    await fetch(`${API}/schedule/classes`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
      body: JSON.stringify(newClass),
    });
    setClasses(prev => [...prev, newClass]);
  } else {
    await fetch(`${API}/schedule/classes/${form.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
      body: JSON.stringify(form),
    });
    setClasses(prev => prev.map(c => c.id === form.id ? { ...form } : c));
  }
};

const handleDeleteClass = async (id) => {
  await fetch(`${API}/schedule/classes/${id}`, {
    method: "DELETE",
    headers: { "Authorization": `Bearer admin123` },
  });
  setClasses(prev => prev.filter(c => c.id !== id));
};

const handleSaveWorkshop = async (form) => {
  const isNew = !workshops.find(w => w.id === form.id);
  if (isNew) {
    const newWS = { ...form, id: `ws_${Date.now()}` };
    await fetch(`${API}/schedule/workshops`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
      body: JSON.stringify(newWS),
    });
    setWorkshops(prev => [...prev, newWS]);
  } else {
    await fetch(`${API}/schedule/workshops/${form.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
      body: JSON.stringify(form),
    });
    setWorkshops(prev => prev.map(w => w.id === form.id ? { ...form } : w));
  }
};

const handleDeleteWorkshop = async (id) => {
  await fetch(`${API}/schedule/workshops/${id}`, {
    method: "DELETE",
    headers: { "Authorization": `Bearer admin123` },
  });
  setWorkshops(prev => prev.filter(w => w.id !== id));
};

const handleCancelDate = async (classId, dateStr) => {
  // 1. Save the schedule cancellation (greys out the calendar pill)
  await fetch(`${API}/schedule/cancellations`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
    body: JSON.stringify({ classId, date: dateStr }),
  });
  setCancelledDates(prev => { const n = new Set(prev); n.add(`${classId}__${dateStr}`); return n; });

  // 2. Cancel all confirmed bookings for this class/date, restore credits, send emails
  await fetch(`${API}/bookings/cancel-class`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
    body: JSON.stringify({ classId, date: dateStr }),
  });

  closeModal();
};
const handleRestoreDate = async (classId, dateStr) => {
  await fetch(`${API}/schedule/cancellations`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
    body: JSON.stringify({ classId, date: dateStr }),
  });
  setCancelledDates(prev => { const n = new Set(prev); n.delete(`${classId}__${dateStr}`); return n; });
  closeModal();
};

const handleSaveDateOverride = async (classId, dateStr, overrides) => {
  await fetch(`${API}/schedule/overrides`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer admin123` },
    body: JSON.stringify({ classId, date: dateStr, ...overrides }),
  });
  setDateOverrides(prev => {
    const next = new Map(prev);
    next.set(`${classId}__${dateStr}`, overrides);
    return next;
  });
};
  const handleEditRecurring = (event) => {
    setShowEditPanel(true);
    // The edit panel will open on the Classes tab; we pass the class to pre-select it
    setEditingClassFromModal(event);
  };

  const renderWorkshopBanners = (date) => {
    const dateStr = toLocalDateStr(date);
    return workshopsForDate(dateStr, workshops).map(w=>(
      <WorkshopBanner key={w.id} workshop={w} dateStr={dateStr} onClick={setSelectedWorkshop} adminMode={adminMode} />
    ));
  };

  return (
    <>
      <div className="cal-wrap">
      {scheduleLoading && <p className="cal-loading">Loading schedule…</p>}
        {/* Header */}
        <div className="cal-header">
          <h2 className="cal-title">
            {viewMode==="month"
              ? <><em>{MONTHS[month]}</em> {year}</>
              : viewMode==="week"
              ? isMobile
                ? <><em>{weekDates[0].toLocaleDateString("en-US",{weekday:"short"})}</em> {weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} &amp; <em>{weekDates[1].toLocaleDateString("en-US",{weekday:"short"})}</em> {weekDates[1].toLocaleDateString("en-US",{day:"numeric"})}</>
                : <><em>Week of</em> {weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})}</>
              : <><em>{DAYS[focusDay.getDay()]}</em> {focusDay.toLocaleDateString("en-US",{month:"long",day:"numeric"})}</>
            }
          </h2>

          <div className="cal-nav">
            <button className="nav-btn" onClick={viewMode==="month"?prevMonth:viewMode==="week"?prevWeek:prevDay}>‹</button>
            <span className="nav-label">
              {viewMode==="month"
                ? `${MONTHS[month].slice(0,3)} ${year}`
                : viewMode==="week"
                ? isMobile
                  ? `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[1].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                  : `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[6].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`
                : focusDay.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})
              }
            </span>
            <button className="nav-btn" onClick={viewMode==="month"?nextMonth:viewMode==="week"?nextWeek:nextDay}>›</button>
          </div>

          <div className="cal-header-right">
            <div className="view-toggle">
              <button data-view="month" className={`toggle-btn ${viewMode==="month"?"active":""}`} onClick={()=>setViewMode("month")}>Month</button>
              <button data-view="week"  className={`toggle-btn ${viewMode==="week" ?"active":""}`} onClick={()=>setViewMode("week")}>Week</button>
              <button data-view="day"   className={`toggle-btn ${viewMode==="day"  ?"active":""}`} onClick={()=>setViewMode("day")}>Day</button>
            </div>
            {adminMode
              ? <button className="admin-add-btn" onClick={()=>setShowEditPanel(true)}>Edit Schedule</button>
              : !hideAdmin && <button className="admin-login-btn" onClick={()=>setShowPwModal(true)}>Admin</button>
            }
          </div>
        </div>

        {/* Month view */}
        {viewMode==="month" && (
          <div className="cal-grid">
            <div className="day-headers">{DAYS.map(d=><div key={d} className="day-header">{d}</div>)}</div>
            <div className="month-grid">
              {calendarCells.map((date,i)=>{
                if(!date) return <div key={`e-${i}`} className="day-cell empty"/>;
                const isToday=isSameDay(date,today);
                const evts=adminMode?getVisibleClasses(date):getPublicClasses(date);
                return (
                  <div key={date.toISOString()} className={`day-cell ${isToday?"today":""}`}>
                    <span className="day-num">{date.getDate()}</span>
                    {renderWorkshopBanners(date)}
                    {evts.map(evt=>(
                     <EventPill key={evt.id} event={evt} compact={true}
                     adminMode={adminMode} cancelled={evt._cancelled}
                     dateStr={toLocalDateStr(date)}
                     onClick={e=>{setSelectedEvent(e);setSelectedDate(date);}} />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Week view */}
        {viewMode==="week" && (
          <div className="week-grid">
            {weekDates.map(date=>{
              const evts=adminMode?getVisibleClasses(date):getPublicClasses(date);
              const dateStr=toLocalDateStr(date);
              const wsForDay=workshopsForDate(dateStr,workshops);
              const isToday=isSameDay(date,today);
              return (
                <div key={date.toISOString()} className={`week-col ${isToday?"today":""}`}>
                  <div className="week-col-header">
                    <span className="week-day-name">{DAYS[date.getDay()]}</span>
                    <span className="week-day-num">{date.getDate()}</span>
                  </div>
                  {wsForDay.map(w => (
                      <WorkshopBanner key={w.id} workshop={w} dateStr={dateStr} onClick={setSelectedWorkshop} adminMode={adminMode} />
                    ))}
                  {evts.map(evt => {
                     const past = isEventPast(dateStr, evt.time, evt.duration);
                     return (
                       <button key={evt.id}
                         className={`week-event-pill ${evt.isHybrid?"hybrid":evt.venue==="venue_online"?"online":"inperson"} ${evt._cancelled?"cancelled-pill":""} ${past && !adminMode?"past-pill":""}`}
                         onClick={() => { if (past && !adminMode) return; setSelectedEvent(evt); setSelectedDate(date); }}>
                         <span className="week-event-time">{formatTime(evt.time)}</span>
                         <span className="week-event-label">{evt._cancelled?"Cancelled":evt.venue==="venue_online"?"Online":"Studio"}</span>
                       </button>
                     );
                   })}
                </div>
              );
            })}
          </div>
        )}

        {/* Day view */}
        {viewMode==="day" && (
          <div className="day-view">
            <div className="day-view-date">
              {focusDay.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}
            </div>
            {(()=>{
              const dateStr=toLocalDateStr(focusDay);
              const evts=adminMode?getVisibleClasses(focusDay):getPublicClasses(focusDay);
              const wsForDay=workshopsForDate(dateStr,workshops);
              const isEmpty = evts.length===0 && wsForDay.length===0;
              return (
                <>
                  {wsForDay.map(w=>(
                    <button key={w.id} className="day-workshop-card" onClick={()=>setSelectedWorkshop(w)}>
                      <div className="day-event-time-col" style={{color:"var(--color-workshop)"}}>Workshop</div>
                      <div className="day-event-info">
                        <span className="day-event-title">{w.title}</span>
                        <span className="day-event-sub">
                          {w.days.find(d=>d.date===dateStr)?.sessions.map(s=>`${s.label} ${formatTime(s.time)}`).join(" · ")}
                        </span>
                      </div>
                    </button>
                  ))}
                  {isEmpty && <p className="no-classes">No classes scheduled today.</p>}
                  {evts.map(evt => {
                      const past = !adminMode && isEventPast(dateStr, evt.time, evt.duration);
                    return (
                        <button key={evt.id}
                        className={`day-event-card ${evt.isHybrid?"hybrid":evt.venue==="venue_online"?"online":"inperson"} ${evt._cancelled?"cancelled-pill":""} ${past?"past-pill":""}`}
                        onClick={() => { if (past && !adminMode) return; setSelectedEvent(evt); setSelectedDate(focusDay); }}>
                        <div className="day-event-time-col">{formatTime(evt.time)}</div>
                        <div className="day-event-info">
                          <span className="day-event-title">
                            {evt.title}{evt._cancelled&&<span className="cancelled-label"> — Cancelled</span>}
                          </span>
                          <span className="day-event-sub">
                            {evt.duration} min · {evt.teacher} · {evt.venue==="venue_online"?"Online":"Journey into Fitness"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}

        {/* Legend */}
        <div className="cal-legend">
          <div className="legend-item"><div className="legend-dot dot-studio"/><span>In Studio</span></div>
          <div className="legend-item"><div className="legend-dot dot-online"/><span>Online</span></div>
          <div className="legend-item"><div className="legend-dot dot-hybrid"/><span>Hybrid</span></div>
          <div className="legend-item"><div className="legend-dot dot-workshop"/><span>Workshop</span></div>
          {adminMode && <div className="legend-item"><div className="legend-dot dot-cancelled"/><span>Cancelled</span></div>}
        </div>
      </div>

      {/* Password */}
        {showPwModal && (
          <PasswordModal
            onSuccess={() => { setShowPwModal(false); window.open('/admin?admin=true', '_blank'); }}
            onClose={() => setShowPwModal(false)}
          />
        )}
      {/* Admin edit panel */}
      {showEditPanel && (
        <AdminEditPanel
          classes={classes} workshops={workshops} venueList={venues}
          initialEditClass={editingClassFromModal}
          onSaveClass={handleSaveClass}       onDeleteClass={handleDeleteClass}
          onSaveWorkshop={handleSaveWorkshop} onDeleteWorkshop={handleDeleteWorkshop}
          onClose={()=>{ setShowEditPanel(false); setEditingClassFromModal(null); }} />
      )}

      {/* Workshop booking (public) */}
      {selectedWorkshop && !adminMode && (
        <WorkshopBookingModal workshop={selectedWorkshop} onClose={closeModal} />
      )}

      {/* Workshop clicked in admin mode — open edit panel */}
      {selectedWorkshop && adminMode && (()=>{ setShowEditPanel(true); setSelectedWorkshop(null); return null; })()}

      {/* Class modals */}
      {selectedEvent && selectedDate && (
        adminMode
          ? <AdminEventModal event={selectedEvent} date={selectedDate}
              cancelledDates={cancelledDates} dateOverrides={dateOverrides}
              venueList={venues}
              onCancelDate={handleCancelDate} onRestoreDate={handleRestoreDate}
              onSaveDateOverride={handleSaveDateOverride}
              onEditRecurring={handleEditRecurring}
              onClose={closeModal} />
          :
              <EventModal event={selectedEvent} date={selectedDate}
                onClose={closeModal} />
      )}
    </>
  );
}
